import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useContext, useRef } from "react";

import FolderConfigDirectoryListPanel from "@/components/folderConfig/FolderConfigDirectoryListPanel";
import FolderConfigEditorPanel from "@/components/folderConfig/FolderConfigEditorPanel";
import FolderConfigStatsBar from "@/components/folderConfig/FolderConfigStatsBar";
import TemplatePathBuilder from "@/components/folderConfig/TemplatePathBuilder";
import {
  folderConfigSearchSchema,
  stripFolderConfigSearchDefaults,
  type FolderConfigSearch,
} from "@/components/folderConfig/folderConfig.utils";
import ContentPage from "@/components/contentPage";
import UserContext from "@/contexts/user";
import { useFolderConfigPage } from "@/hooks/useFolderConfigPage";

export const Route = createFileRoute("/admin/folder-config/")({
  component: FolderConfigAdminPage,
  validateSearch: folderConfigSearchSchema,
});

function LoadingState() {
  return (
    <div className="flex h-full w-full items-center justify-center py-10 text-blue-200">
      Loading...
    </div>
  );
}

function UnauthorizedState() {
  return (
    <div className="mx-7 rounded-xl border border-amber-100 bg-amber-50 p-6 text-amber-700">
      You need real asset creator permissions to edit folder configs.
    </div>
  );
}

function FolderConfigAdminPage() {
  const { api } = Route.useRouteContext();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const { user } = useContext(UserContext);
  const builderRef = useRef<HTMLDivElement | null>(null);

  const updateSearch = useCallback(
    (
      updater:
        | Partial<FolderConfigSearch>
        | ((prev: FolderConfigSearch) => FolderConfigSearch),
      options?: { replace?: boolean }
    ) => {
      void navigate({
        replace: options?.replace ?? true,
        search: ((prev: FolderConfigSearch) => {
          const nextSearch =
            typeof updater === "function"
              ? updater(prev)
              : { ...prev, ...updater };

          return stripFolderConfigSearchDefaults(nextSearch);
        }) as never,
      });
    },
    [navigate]
  );

  const { status, stats, listPanel, editorPanel } = useFolderConfigPage({
    api,
    search,
    updateSearch,
    user,
    builderRef,
  });

  if (status === "loading") {
    return (
      <ContentPage title={"Folder Config"}>
        <LoadingState />
      </ContentPage>
    );
  }

  if (status === "unauthorized") {
    return (
      <ContentPage title={"Folder Config"}>
        <UnauthorizedState />
      </ContentPage>
    );
  }

  return (
    <ContentPage title={"Folder Config"}>
      <div className="mx-7 flex min-h-[calc(100vh-130px)] flex-col gap-4">
        <FolderConfigStatsBar {...stats} />

        <div className="grid flex-1 grid-cols-1 gap-5 xl:grid-cols-[minmax(20rem,0.75fr)_minmax(0,1.25fr)]">
          <FolderConfigDirectoryListPanel {...listPanel} />

          <FolderConfigEditorPanel
            editingDirectory={editorPanel.editingDirectory}
            showForm={editorPanel.showForm}
            isDuplicateDocumentedPath={editorPanel.isDuplicateDocumentedPath}
            previewStatus={editorPanel.previewStatus}
            previewErrorMessage={editorPanel.previewErrorMessage}
            previewData={editorPanel.previewData}
            isPreviewPending={editorPanel.isPreviewPending}
            templatePath={editorPanel.templatePath}
            previewConcreteMatches={editorPanel.previewConcreteMatches}
            selectedPreviewMatch={editorPanel.selectedPreviewMatch}
            isPreviewExampleOpen={editorPanel.isPreviewExampleOpen}
            onTogglePreviewExampleOpen={editorPanel.onTogglePreviewExample}
            onShowNextPreviewMatch={editorPanel.onShowNextPreviewMatch}
            observedSubdirectories={editorPanel.observedSubdirectories}
            observedFiles={editorPanel.observedFiles}
            previewContentsLoading={editorPanel.previewContentsLoading}
            previewContentsEmpty={editorPanel.previewContentsEmpty}
            builder={
              <div ref={builderRef}>
                <TemplatePathBuilder
                  pathSegments={editorPanel.pathSegments}
                  segmentComposer={editorPanel.segmentComposer}
                  assetSearchQuery={editorPanel.assetSearchQuery}
                  filteredRealAssets={editorPanel.filteredRealAssets}
                  realAssetsById={editorPanel.realAssetsById}
                  exampleDeploymentIdsByAssetId={
                    editorPanel.exampleDeploymentIdsByAssetId
                  }
                  onToggleChooser={editorPanel.onToggleSegmentTypeChooser}
                  onOpenLiteralComposer={editorPanel.onOpenLiteralComposer}
                  onLiteralDraftChange={
                    editorPanel.onLiteralComposerDraftChange
                  }
                  onCommitLiteral={editorPanel.onCommitLiteralSegment}
                  onOpenRealAssetPicker={editorPanel.onOpenRealAssetPicker}
                  onInsertOrReplaceToken={
                    editorPanel.onInsertOrReplaceTokenSegment
                  }
                  onCommitExistingLiteral={
                    editorPanel.onCommitExistingLiteralSegment
                  }
                  onRemoveSegment={editorPanel.onRemovePathSegment}
                  onCloseComposer={editorPanel.onCloseSegmentComposer}
                  onAssetSearchChange={editorPanel.onAssetSearchQueryChange}
                />
              </div>
            }
            descriptionSeed={editorPanel.descriptionSeed}
            descriptionEditorResetKey={editorPanel.descriptionEditorResetKey}
            hasDescription={editorPanel.hasDescription}
            onDescriptionChange={editorPanel.onDescriptionChange}
            allowAddendumUpload={editorPanel.allowAddendumUpload}
            onAllowAddendumUploadChange={
              editorPanel.onAllowAddendumUploadChange
            }
            selectedDataExpertUserId={editorPanel.selectedDataExpertUserId}
            selectedDataExpertLabel={editorPanel.selectedDataExpertLabel}
            directoryUserOptions={editorPanel.directoryUserOptions}
            directoryDocumentersLoading={editorPanel.isDirectoryUsersLoading}
            onSelectedDataExpertChange={editorPanel.onSelectedDataExpertChange}
            onStartNew={editorPanel.onResetWorkspace}
            canSubmit={editorPanel.canSubmit}
            saveButtonLabel={editorPanel.saveButtonLabel}
            saveHelpText={
              editorPanel.isDuplicateDocumentedPath
                ? "This path is already documented."
                : editorPanel.editingDirectory
                ? "Save your changes when ready."
                : ""
            }
            onSave={editorPanel.onSave}
          />
        </div>
      </div>
    </ContentPage>
  );
}
